import { useEffect, useState } from 'react';
import { useEnv } from './useEnv';

/**
 * hook to get the business partner details from the BPPR or cos
 * v1 - useBPName() returns the business partner details from internal cloud storage(COS)
 * v2 - useBPName(UT30WidgetCode, true) returns business partner details from BPPR with sticky feature
 *
 * @param widgetName - set the UT30 widget code to get the business partner details from bppr and make bpid sticky
 * @param bppr - set to true to enable bppr api call
 * @returns {
 *  bpName - business partner name
 *  logo - business partner logo,
 *  companyId - business partner id or ceid
 *  desc - business partner descrption
 *  mssp - this is mssp flag which is either true or false, preset by the business partners
 * } business partner details
 */

type BPType = {
  companyId: string;
  logo: string;
  bpName: string;
  desc: string;
  mssp: string;
};

export const useBPName = (widgetName = '', bppr = false) => {
  const [bpName, setBpName] = useState('');
  const [logo, setLogo] = useState('');
  const [desc, setDesc] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [bpList, setBpList] = useState<BPType[]>([]);
  const [mssp, setMssp] = useState('');
  const [environment] = useEnv();

  const getBPListUrl = (bppr: boolean) => {
    if (bppr) {
      // to be enhanced when BPPR API is integrated, only MVP for now
      return environment === 'production'
        ? 'https://cos-dsh-qradar-bp.s3.eu-de.cloud-object-storage.appdomain.cloud/bpList_Logo.json'
        : 'https://cos-dsh-qradar-bp.s3.eu-de.cloud-object-storage.appdomain.cloud/bpList_Logo_test.json';
    }
    return 'https://cos-dsh-qradar-bp.s3.eu-de.cloud-object-storage.appdomain.cloud/bpList.json';
  };

  useEffect(() => {
    const idRetrieved =
      new URLSearchParams(window.location.search).get('bpid') ||
      sessionStorage.getItem(widgetName);
    if (idRetrieved) {
      sessionStorage.setItem(widgetName, idRetrieved); // to make the bpid sticky
      setCompanyId(idRetrieved);
      fetch(getBPListUrl(bppr ? true : false), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
        },
      })
        .then((res) => res.json())
        .then((data) => {
          setBpList(bppr ? data.businesspartners : data);
        })
        .catch((error) => {
          console.log(`Fetching error for BPPR: ${error}`);
        });
    }
  }, [environment]);

  useEffect(() => {
    const bp = bpList.find((bp) => bp.companyId === companyId);
    if (bp && Object.keys(bp).length) {
      setBpName(bp.bpName);
      // Find BP Logo
      const bpImg = bpList.find((bpImg: BPType) => bpImg.logo !== '');
      if (bpImg) {
        setLogo(bp.logo);
      }

      // Find BP Description
      const bpDesc = bpList.find((bpDesc: BPType) => bpDesc.desc !== '');
      if (bpDesc) {
        setDesc(bp.desc);
      }
      // Find BP MMSP=yes
      const bpMssp = bpList.find((bpMssp: BPType) => bpMssp.mssp === 'yes');
      if (bpMssp) {
        setMssp(bp.mssp);
      }
    }
  }, [bpList, companyId]);

  return { bpName, logo, companyId, desc, mssp };
};
